import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { auth } from '../firebase';

// ============================================
// CONFIGURAÇÃO
// ============================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://qflow.xflow.ca:5005';
const AUTH_API_BASE_URL = import.meta.env.VITE_API_BASE_URL_TOKEN || 'https://qflow-1041194960740.us-west1.run.app';
const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === 'true';

// ============================================
// CRIAR INSTÂNCIAS DO AXIOS
// ============================================

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/site/api/hub`,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Para enviar cookies se necessário
});

export const authClient = axios.create({
  baseURL: AUTH_API_BASE_URL, // Usar a URL correta para autenticação
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
});

authClient.interceptors.request.use(
    (config) => {
      // Para o authClient, sempre use a URL de autenticação
      config.baseURL = AUTH_API_BASE_URL;

      const token = localStorage.getItem('jwt_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
);

// ============================================
// INTERCEPTOR DE REQUEST (BLINDADO)
// ============================================

apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('userCode');

      // Rotas de autenticação vão para o servidor de auth, não para o hub
      const isAuthRoute = config.url === '/Usuario' || config.url === '/login';
      if (isAuthRoute) {
        config.baseURL = AUTH_API_BASE_URL;
        if (config.url === '/Usuario') {
          const token = localStorage.getItem('jwt_token');
          if (token) config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      }

      config.baseURL = `${API_BASE_URL}/site/api/hub`;

      // ── Injeta Firebase ID Token em TODAS as chamadas ao hub ──
      // getIdToken() renova automaticamente se o token estiver expirado (validade: 1h)
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const idToken = await currentUser.getIdToken();
          config.headers.Authorization = `Bearer ${idToken}`;
          localStorage.setItem('jwt_token', idToken); // mantém localStorage atualizado
        } else {
          // Sem usuário Firebase — usa token salvo como fallback
          const stored = localStorage.getItem('jwt_token');
          if (stored) config.headers.Authorization = `Bearer ${stored}`;
        }
      } catch {
        // Falha ao renovar — tenta com token armazenado
        const stored = localStorage.getItem('jwt_token');
        if (stored) config.headers.Authorization = `Bearer ${stored}`;
      }

      // ── Injeta empresa (tenantId) em todas as chamadas ──
      if (tenantId) {
        const method = (config.method || '').toLowerCase();
        if (method === 'get') {
          config.params = { ...config.params, empresa: tenantId };
        } else if (['post', 'put', 'delete', 'patch'].includes(method)) {
          if (config.data instanceof FormData) {
            // FormData: não destruir — apenas adiciona empresa se não existir
            if (!config.data.has('empresa')) {
              config.data.append('empresa', tenantId);
            }
            // Deixa o axios definir Content-Type com o boundary correto
            delete config.headers['Content-Type'];
          } else {
            const body = config.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : {};
            config.data = { ...body, empresa: tenantId };
          }
        }
      } else {
        console.error("⚠️ ERRO: Tentativa de chamada à API sem 'tenantId' no localStorage. URL:", config.url);
      }

      return config;
    },
    (error) => Promise.reject(error)
);

// ============================================
// INTERCEPTOR DE RESPONSE
// ============================================

apiClient.interceptors.response.use(
    (response) => {
      // Debug
      if (DEBUG_MODE) {
        console.log('✅ API Response:', {
          status: response.status,
          url: response.config.url,
          data: response.data,
        });
      }
      return response;
    },
    (error: AxiosError) => {
      // Debug
      if (DEBUG_MODE) {
        console.error('❌ API Error:', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data,
        });
      }

      // Mensagens de erro amigáveis
      let errorMessage = 'Erro ao comunicar com o servidor';

      if (error.response) {
        // Servidor respondeu com erro
        const status = error.response.status;
        const data: any = error.response.data;

        switch (status) {
          case 400:
            errorMessage = data?.error || 'Requisição inválida';
            break;
          case 401:
            errorMessage = 'Não autorizado. Faça login novamente.';
            break;
          case 403:
            errorMessage = 'Acesso negado';
            break;
          case 404:
            errorMessage = 'Recurso não encontrado';
            break;
          case 500:
            errorMessage = 'Erro interno do servidor';
            break;
          case 503:
            errorMessage = 'Servidor temporariamente indisponível';
            break;
          default:
            errorMessage = data?.error || data?.message || 'Erro desconhecido';
        }
      } else if (error.request) {
        // Requisição foi feita mas não houve resposta
        errorMessage = 'Servidor não respondeu. Verifique sua conexão com a internet.';
      } else {
        // Erro ao configurar a requisição
        errorMessage = error.message || 'Erro ao fazer requisição';
      }

      // Adicionar mensagem amigável ao objeto de erro
      error.message = errorMessage;

      return Promise.reject(error);
    }
);

// ============================================
// HELPERS
// ============================================

/**
 * Recupera o código seguro (UUID) do usuário atual dos armazenamentos locais
 */
export const getUserCode = (): string => {
  return localStorage.getItem('tenantId') ||
      localStorage.getItem('userCode') ||
      localStorage.getItem('id_cliente_hub') ||
      'TEST_USER';
};

/**
 * Define o código do tenant para sessões futuras
 */
export const setUserCode = (tenantId: string): void => {
  localStorage.setItem('tenantId', tenantId);
};

/**
 * Remove o código do usuário (logout)
 */
export const clearUserCode = (): void => {
  localStorage.removeItem('tenantId');
  localStorage.removeItem('userCode');
  localStorage.removeItem('id_cliente_hub');
  localStorage.removeItem('userData');
};

/**
 * Retorna a URL base configurada para a API
 */
export const getApiBaseUrl = (): string => {
  return API_BASE_URL;
};

// ============================================
// EXPORT
// ============================================

export default apiClient;
